import { AlertCircle, CheckCircle } from "lucide-react";
import { CANONICAL_COLUMNS } from "./Columns";

export const ValidationSuccess = ({ count }) => {
  return (
    <div className="p-4 bg-green-50 rounded-md border border-green-200">
      <div className="flex items-center mb-2">
        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
        <p className="font-semibold text-green-800">
          File Validated Successfully
        </p>
      </div>
      <p className="text-sm text-green-700">{count} parts ready for upload</p>
    </div>
  );
};
export const ValidationErrors = ({ errors }) => {
  console.log("errors:", errors);
  if (!errors || errors.length === 0) return null;

  // Check if array contains simple strings (flat errors)
  const isFlatErrorArray = typeof errors[0] === "string";

  return (
    <div className="p-4 bg-red-50 rounded-md border border-red-300">
      <div className="flex items-center mb-3">
        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
        <p className="font-semibold text-red-800 text-lg">
          ⚠️ Validation Errors Found ({errors.length}{" "}
          {isFlatErrorArray ? "items" : "rows"})
        </p>
      </div>
      <p className="text-sm text-red-700 mb-3">
        {isFlatErrorArray
          ? "Please fix the following validation issues before proceeding:"
          : "The following rows contain errors and will NOT be uploaded:"}
      </p>

      <div className="max-h-60 overflow-y-auto space-y-2">
        {isFlatErrorArray ? (
          // 🧩 Case 1: Array of simple strings
          <ul className="list-disc ml-5 space-y-1 text-sm text-red-800">
            {errors.map((err, idx) => (
              <li
                key={idx}
                className="bg-red-100 p-2 rounded border border-red-200"
              >
                {err}
              </li>
            ))}
          </ul>
        ) : (
          // 🧩 Case 2: Array of error objects
          <>
            {errors.slice(0, 15).map((error, index) => (
              <div
                key={index}
                className="text-sm text-red-800 bg-red-100 p-3 rounded shadow-sm border border-red-200"
              >
                <p className="font-semibold mb-1">Row {error.rowNumber}:</p>
                <div className="space-y-1">
                  {error?.errors?.map((err, errIndex) => (
                    <p key={errIndex} className="text-xs text-red-700 ml-2">
                      • {err}
                    </p>
                  ))}
                </div>
                {error.rowData && (
                  <div className="mt-2 pt-2 border-t border-red-200">
                    <p className="text-xs text-red-600 font-mono">
                      {error.rowData?.[CANONICAL_COLUMNS.YEAR]}{" "}
                      {error.rowData?.[CANONICAL_COLUMNS.MAKE]}{" "}
                      {error.rowData?.[CANONICAL_COLUMNS.MODEL]} -{" "}
                      {error.rowData?.[CANONICAL_COLUMNS.PART_NAME]}
                    </p>
                  </div>
                )}
              </div>
            ))}
            {errors.length > 15 && (
              <p className="text-sm text-red-800 font-medium mt-2 bg-red-100 p-2 rounded">
                ... and {errors.length - 15} more rows with errors
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-3 p-3 bg-red-100 rounded border border-red-200">
        <p className="text-sm text-red-800 font-medium">
          💡 <strong>What to do:</strong> Fix the errors in your CSV file and
          re-upload, or proceed to upload only the valid rows.
        </p>
      </div>
    </div>
  );
};

const validateYear = (year) => {
  if (!year || year.toString().trim() === "") {
    return { isValid: false, error: "Year is required" };
  }

  const yearStr = year.toString().trim();
  const yearInt = parseInt(yearStr);
  const currentYear = new Date().getFullYear();

  if (isNaN(yearInt)) {
    return { isValid: false, error: `'${yearStr}' is not a valid year` };
  }

  if (yearInt < 1900) {
    return {
      isValid: false,
      error: `Year ${yearInt} is too old (must be 1900 or later)`
    };
  }

  if (yearInt > currentYear + 1) {
    return { isValid: false, error: `Year ${yearInt} is in the future` };
  }

  return { isValid: true, error: null };
};

const validatePrice = (price) => {
  if (!price || price.toString().trim() === "") {
    return { isValid: false, error: "Price is required" };
  }

  const priceStr = price.toString().trim().replace(/[$,]/g, "");
  const priceFloat = parseFloat(priceStr);

  if (isNaN(priceFloat)) {
    return { isValid: false, error: `'${price}' is not a valid price` };
  }

  if (priceFloat < 0) {
    return { isValid: false, error: "Price cannot be negative" };
  }

  if (priceFloat === 0) {
    return { isValid: false, error: "Price must be greater than 0" };
  }

  if (priceFloat > 1000000) {
    return { isValid: false, error: "Price seems unusually high" };
  }

  return { isValid: true, error: null };
};

const validateQuantity = (quantity) => {
  if (
    quantity === undefined ||
    quantity === null ||
    quantity.toString().trim() === ""
  ) {
    return { isValid: false, error: "Quantity is required" };
  }

  const qtyStr = quantity.toString().trim();
  const quantityInt = parseInt(qtyStr);

  if (isNaN(quantityInt)) {
    return { isValid: false, error: `'${quantity}' is not a valid quantity` };
  }

  if (quantityInt < 0) {
    return { isValid: false, error: "Quantity cannot be negative" };
  }

  if (quantityInt > 10000) {
    return { isValid: false, error: "Quantity seems unusually high" };
  }

  return { isValid: true, error: null };
};

const validateRequiredField = (value, fieldName) => {
  if (!value || value.toString().trim() === "") {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const strValue = value.toString().trim();

  if (strValue.length > 200) {
    return {
      isValid: false,
      error: `${fieldName} is too long (max 200 characters)`
    };
  }

  return { isValid: true, error: null };
};

const validatePostalCode = (postalCode) => {
  if (!postalCode || postalCode.toString().trim() === "") {
    return { isValid: false, error: "Postal code is required" };
  }

  const code = postalCode.toString().trim();

  if (code.length < 3 || code.length > 10) {
    return { isValid: false, error: "Postal code must be 3-10 characters" };
  }

  return { isValid: true, error: null };
};

export const validateRowData = (row) => {
  const errors = [];

  const yearValidation = validateYear(row[CANONICAL_COLUMNS.YEAR]);
  if (!yearValidation.isValid) errors.push(yearValidation.error);

  const makeValidation = validateRequiredField(
    row[CANONICAL_COLUMNS.MAKE],
    "Make"
  );
  if (!makeValidation.isValid) errors.push(makeValidation.error);

  const modelValidation = validateRequiredField(
    row[CANONICAL_COLUMNS.MODEL],
    "Model"
  );
  if (!modelValidation.isValid) errors.push(modelValidation.error);

  const partNameValidation = validateRequiredField(
    row[CANONICAL_COLUMNS.PART_NAME],
    "Part Name"
  );
  if (!partNameValidation.isValid) errors.push(partNameValidation.error);

  const categoryValidation = validateRequiredField(
    row[CANONICAL_COLUMNS.PART_CATEGORY],
    "Part Category"
  );
  if (!categoryValidation.isValid) errors.push(categoryValidation.error);

  const priceValidation = validatePrice(row[CANONICAL_COLUMNS.PRICE]);
  if (!priceValidation.isValid) errors.push(priceValidation.error);

  const qtyValidation = validateQuantity(row[CANONICAL_COLUMNS.QUANTITY]);
  if (!qtyValidation.isValid) errors.push(qtyValidation.error);

  const postalValidation = validatePostalCode(
    row[CANONICAL_COLUMNS.POSTAL_CODE]
  );
  if (!postalValidation.isValid) errors.push(postalValidation.error);

  return errors;
};

export const validateStripePaymentLink = (link) => {
  // If no link provided (null, undefined, or empty string), it's valid (optional field)
  if (!link || typeof link !== "string" || link.trim() === "") {
    return { isValid: true, error: null };
  }

  const trimmedLink = link.trim();

  // Check if link starts with https://
  if (!trimmedLink.startsWith("https://")) {
    return {
      isValid: false,
      error: "Payment link must start with 'https://'"
    };
  }

  // Check if it's a Stripe domain
  const stripeDomainRegex =
    /^https:\/\/(buy\.stripe\.com|checkout\.stripe\.com)\//;

  if (!stripeDomainRegex.test(trimmedLink)) {
    return {
      isValid: false,
      error:
        "Only Stripe payment links are allowed (buy.stripe.com or checkout.stripe.com)"
    };
  }

  // Validate specific Stripe link patterns
  const stripePatterns = [
    {
      pattern: /^https:\/\/buy\.stripe\.com\/[a-zA-Z0-9_-]+$/,
      type: "Production Buy"
    },
    {
      pattern: /^https:\/\/checkout\.stripe\.com\/[a-zA-Z0-9_\/-]+$/,
      type: "Production Checkout"
    },
    {
      pattern: /^https:\/\/buy\.stripe\.com\/test_[a-zA-Z0-9_-]+$/,
      type: "Test Buy"
    },
    {
      pattern: /^https:\/\/checkout\.stripe\.com\/c\/pay\/[a-zA-Z0-9_-]+$/,
      type: "Checkout Pay"
    }
  ];

  for (const { pattern, type } of stripePatterns) {
    if (pattern.test(trimmedLink)) {
      return { isValid: true, error: null, type };
    }
  }

  return {
    isValid: false,
    error: "Not a valid Stripe payment link format"
  };
};
